import { Box } from "@mui/material";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import { useUploadFileMutation } from "../app/utilsApiSlice";
import EditorToolbar from "../components/pages/lms/teacher/notes/EditorToolbar";
import "../components/pages/lms/teacher/notes/EditorStyles.css";
import { useEffect } from "react";

const CustomTipTapEditor = ({
  value = "",
  onChange,
  placeholder = "Enter text here...",
  showToolbar = true,
  minHeight = "250px",
}) => {
  const [uploadFile] = useUploadFileMutation();

  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadFile(formData).unwrap();
      return response.url; // Return the URL for TipTap to insert
    } catch (error) {
      console.error("Image upload failed:", error);
      return null;
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Image.configure({
        allowBase64: false,
        inline: false,
        HTMLAttributes: {
          class: "tiptap-image resizable-image",
        },
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: null,
              parseHTML: (element) => element.getAttribute("width"),
              renderHTML: (attributes) => {
                if (!attributes.width) {
                  return {};
                }
                return {
                  width: attributes.width,
                };
              },
            },
            height: {
              default: null,
              parseHTML: (element) => element.getAttribute("height"),
              renderHTML: (attributes) => {
                if (!attributes.height) {
                  return {};
                }
                return {
                  height: attributes.height,
                };
              },
            },
          };
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
  });

  // Keep editor content in sync when `value` prop changes (e.g., when form is reset)
  useEffect(() => {
    if (!editor) return;
    try {
      const current = editor.getHTML();
      if ((value || "") !== (current || "")) {
        editor.commands.setContent(value || "");
      }
    } catch (err) {
      // ignore errors when editor isn't ready
    }
  }, [value, editor]);

  // Add image resizing functionality
  useEffect(() => {
    if (!editor) return;

    const handleMouseDown = (e) => {
      const img = e.target;
      if (!img.classList.contains("resizable-image")) return;

      const startX = e.clientX;
      const startWidth = img.offsetWidth;
      const startHeight = img.offsetHeight;

      const handleMouseMove = (moveEvent) => {
        const diffX = moveEvent.clientX - startX;

        const aspectRatio = startWidth / startHeight;
        const newWidth = Math.max(100, startWidth + diffX);
        const newHeight = newWidth / aspectRatio;

        img.style.width = newWidth + "px";
        img.style.height = newHeight + "px";
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    const editorElement = document.querySelector(".ProseMirror");
    if (editorElement) {
      editorElement.addEventListener("mousedown", handleMouseDown);
      return () => {
        editorElement.removeEventListener("mousedown", handleMouseDown);
      };
    }
  }, [editor]);

  return (
    <Box>
      {showToolbar && <EditorToolbar editor={editor} onImageUpload={handleImageUpload} />}
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E7EAE9"}`,
          borderRadius: "10px",
          overflow: "hidden",
          "& .ProseMirror": {
            minHeight: minHeight,
            padding: "12px 16px",
            outline: "none",
          },
          "& .ProseMirror.is-editor-empty:first-child::before": {
            content: `"${placeholder}"`,
            float: "left",
            color: "#adb5bd",
            pointerEvents: "none",
            height: 0,
          },
        })}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default CustomTipTapEditor;
